"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CashCounter } from "@/components/ui/cash-counter";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import { cn } from "@/lib/utils";

const savingsSchema = z.object({
  downPayment: z.number().min(0),
  downPaymentPercentage: z.number().min(0).max(100),
});

type SavingsForm = z.infer<typeof savingsSchema>;

const LOAN_TYPES = [
  {
    percent: 3.5,
    label: "FHA Loan",
    color: "bg-blue-500",
    tooltip:
      "A government-backed loan with lower down payment requirements, ideal for first-time homebuyers. Requires mortgage insurance.",
  },
  {
    percent: 5,
    label: "Conventional",
    color: "bg-indigo-500",
    tooltip:
      "A traditional mortgage loan with a minimum 5% down payment. Monthly PMI required until 20% equity is reached.",
  },
  {
    percent: 20,
    label: "No PMI",
    color: "bg-green-500",
    tooltip:
      "With 20% or more down, you avoid Private Mortgage Insurance (PMI), lowering your monthly payments.",
  },
];

export function SavingsStrategyForm() {
  const { journeyData, updateJourney } = useJourneyData();
  const { toast } = useToast();
  const [isSliderActive, setIsSliderActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const previousValueRef = useRef<number>();
  const userInitiatedRef = useRef(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout>();

  const budget = journeyData?.budget || 0;
  const initialPercentage = journeyData?.downPayment
    ? (journeyData.downPayment / budget) * 100
    : 20;

  const form = useForm<SavingsForm>({
    resolver: zodResolver(savingsSchema),
    values: {
      downPaymentPercentage: initialPercentage,
      downPayment: (initialPercentage / 100) * budget,
    },
  });

  const percentage = form.watch("downPaymentPercentage");
  const downPayment = (percentage / 100) * budget;

  const handleSliderChange = (value: number[]) => {
    userInitiatedRef.current = true;
    const newPercentage = value[0];
    form.setValue("downPaymentPercentage", newPercentage);
    form.setValue("downPayment", (newPercentage / 100) * budget);
  };

  useEffect(() => {
    if (isDragging) {
      previousValueRef.current = downPayment;
      return;
    }

    if (!userInitiatedRef.current) return;
    if (previousValueRef.current === downPayment) return;

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      updateJourney.mutate(
        { downPayment },
        {
          onSuccess: () => {
            toast({
              title: "Down Payment Updated",
              description: `Your down payment has been set to $${downPayment.toLocaleString()} (${percentage.toFixed(1)}%)`,
              variant: "default",
            });
          },
        },
      );
      previousValueRef.current = downPayment;
    }, 500);

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [isDragging, downPayment, percentage, updateJourney, toast]);

  useEffect(() => {
    if (journeyData?.downPayment) {
      const newPercentage = (journeyData.downPayment / budget) * 100;
      form.reset({
        downPaymentPercentage: newPercentage,
        downPayment: journeyData.downPayment,
      });
    }
  }, [journeyData?.downPayment, budget, form]);

  const getMonthlyPaymentEstimate = () => {
    const loanAmount = budget - downPayment;
    const monthlyInterestRate = 0.06 / 12; // Assuming 6% interest rate
    const numberOfPayments = 30 * 12; // 30-year mortgage

    const monthlyPayment =
      (loanAmount *
        monthlyInterestRate *
        (1 + monthlyInterestRate) ** numberOfPayments) /
      ((1 + monthlyInterestRate) ** numberOfPayments - 1);

    return Math.round(monthlyPayment);
  };

  const getSliderColor = (percentage: number) => {
    if (percentage >= 20) return "bg-green-500";
    if (percentage >= 5) return "bg-indigo-500";
    return "bg-blue-500";
  };

  return (
    <TooltipProvider>
      <Form {...form}>
        <form className="space-y-8 max-w-2xl mx-auto">
          <FormField
            control={form.control}
            name="downPaymentPercentage"
            render={({ field }) => (
              <FormItem className="space-y-10">
                <div className="text-center space-y-2">
                  <FormLabel className="text-2xl sm:text-3xl font-bold text-gray-900 block">
                    How much can you put down?
                  </FormLabel>
                  <p className="text-gray-500">
                    For a ${budget.toLocaleString()} home
                  </p>
                </div>

                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center space-y-2"
                >
                  <CashCounter
                    value={downPayment}
                    className="text-5xl sm:text-6xl font-bold text-gray-900"
                    baseDelay={50}
                    skipInitialAnimation={!!journeyData?.downPayment}
                  />
                  <div className="text-xl text-gray-600 font-semibold">
                    {percentage.toFixed(1)}% down
                  </div>
                  <div className="text-sm text-gray-500">
                    Est. monthly payment: $
                    {getMonthlyPaymentEstimate().toLocaleString()}
                  </div>
                </motion.div>

                <FormControl>
                  <div className="relative w-[85%] mx-auto pt-8">
                    {/* Slider section */}
                    <div
                      onMouseEnter={() => setIsSliderActive(true)}
                      onMouseLeave={() => {
                        setIsSliderActive(false);
                        setIsDragging(false);
                      }}
                      onMouseDown={() => setIsDragging(true)}
                      onMouseUp={() => setIsDragging(false)}
                      onTouchStart={() => setIsDragging(true)}
                      onTouchEnd={() => setIsDragging(false)}
                    >
                      <Slider
                        value={[field.value]}
                        min={0}
                        max={100}
                        step={0.5}
                        onValueChange={handleSliderChange}
                        className="py-4"
                        classNames={{
                          track: "h-2 bg-gray-200",
                          range: cn("h-2", getSliderColor(percentage)),
                          thumb: cn(
                            "h-8 w-8 border-2 bg-white",
                            getSliderColor(percentage).replace(
                              "bg-",
                              "border-",
                            ),
                            "shadow-[0_2px_10px_rgba(0,0,0,0.1)]",
                            isSliderActive &&
                              "scale-110 shadow-[0_4px_16px_rgba(0,0,0,0.2)]",
                            "transition-all duration-200",
                          ),
                        }}
                      />
                    </div>

                    {/* Labels with tooltips */}
                    <div className="flex justify-between text-sm mt-4">
                      {LOAN_TYPES.map((loan) => (
                        <Tooltip key={loan.percent}>
                          <TooltipTrigger asChild>
                            <div
                              className="text-center cursor-help"
                              style={{
                                width: "120px",
                                marginLeft: loan.percent === 3.5 ? "-60px" : 0,
                                marginRight: loan.percent === 20 ? "-60px" : 0,
                              }}
                            >
                              <div className="font-semibold text-gray-700">
                                {loan.label}
                              </div>
                              <div className="text-xs text-gray-500">
                                {loan.percent}%
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            className="max-w-[200px] text-sm p-3"
                            side="bottom"
                            align="center"
                          >
                            {loan.tooltip}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </TooltipProvider>
  );
}
